// DOCS → REQUIREMENTS → TESTS — the tests that PROVE one requirement.
//
// A requirement carries any number of executable tests (cortex test engine,
// shared/test-engine): declarative specs the same runner executes inside
// cortex, inside a deployable archive, or anywhere else. This component is the
// section inside an expanded requirement row: every test with its newest
// verdict (where and when it ran), a Run for just this requirement's tests,
// "Write tests" that asks the agent fleet to derive tests from the use cases,
// and a small form to add or edit a test by hand.
//
// The vocabulary is the engine's: passed / failed / error / skipped. `error`
// means the test could not run (the endpoint threw, the query errored); it is
// kept apart from `failed` (the check ran and did not hold) because the two
// call for different people.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, Play, Plus, Pencil, Trash2, Sparkles, ChevronDown, ChevronRight, Activity, FlaskConical,
} from "lucide-react";
import { fetchData } from "../../fetchapi/fetchapi";
import { createPageUrl } from "@/utils";

export const TEST_STATUS = {
  passed: { label: "passed", dot: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-400" },
  failed: { label: "failed", dot: "bg-red-500", text: "text-red-700 dark:text-red-400" },
  error: { label: "error", dot: "bg-amber-500", text: "text-amber-700 dark:text-amber-400" },
  skipped: { label: "skipped", dot: "bg-slate-400", text: "text-page-muted" },
  none: { label: "never run", dot: "bg-transparent border border-page-divider", text: "text-page-muted" },
};
export const testStatusMeta = (s) => TEST_STATUS[s] || TEST_STATUS.none;

export function StatusDot({ status, size = 8, title }) {
  const m = testStatusMeta(status);
  return <span title={title || m.label} data-status={status || "none"}
    className={`inline-block rounded-full shrink-0 ${m.dot}`} style={{ width: size, height: size }} />;
}

// Small aggregate for a row header: "3 tests · 2 ✓ 1 ✗".
export function summarize(tests) {
  const c = { total: 0, passed: 0, failed: 0, error: 0, skipped: 0, none: 0 };
  for (const t of tests || []) { c.total++; c[t.latest?.status || "none"] = (c[t.latest?.status || "none"] || 0) + 1; }
  return c;
}
export function TestsBadge({ tests, testid }) {
  const c = summarize(tests);
  if (!c.total) return null;
  const worst = c.error ? "error" : c.failed ? "failed" : c.none === c.total ? "none" : c.passed ? "passed" : "skipped";
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 text-[11px] ${testStatusMeta(worst).text}`}
      title={`${c.total} test(s): ${c.passed} passed, ${c.failed} failed, ${c.error} error(s), ${c.skipped} skipped, ${c.none} never run`}>
      <StatusDot status={worst} /> {c.total} test{c.total === 1 ? "" : "s"}
      {c.failed || c.error ? ` · ${c.failed + c.error} failing` : c.passed === c.total ? " · all passing" : ""}
    </span>
  );
}

export const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 90) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  return d.toLocaleString();
};

const KIND_HELP = {
  endpoint: "Run one of the project's Flow endpoints and check its payload.",
  http: "Call a URL on a named target (app, portal_api, dashboard, endpoints) and check status/body.",
  sql: "Query the project's postgres target (read-only) and check the rows.",
  browser: "Drive the app in a real headless browser: declarative steps (goto a view, click, fill, expect text/count, screenshot). Runs where a browser exists — the agent fleet and deployable archives; screenshots become evidence on this requirement.",
  agent: "Ask one of the project's agents a question and check what it answered — and the guardrail verdict the platform recorded over the answer (subject: answer, refused, guardrail.verdict, tool_calls). One model run per test; runs on demand and after a deploy.",
};
const TEMPLATES = {
  endpoint: { endpoint: "cortex.example.endpoint", input: {}, expect: [{ path: "$.payload", op: "exists" }] },
  http: { target: "app", path: "/", method: "GET", expect: [{ path: "$.status", op: "eq", value: 200 }] },
  sql: { query: "SELECT count(*) AS n FROM some_table", expect: [{ path: "$.rows[0].n", op: "gte", value: 1 }] },
  agent: { agent: "workflows", prompt: "How many open cases are there right now?",
    expect: [{ path: "$.refused", op: "falsy" }, { path: "$.answer", op: "not_matches", value: "/I don't know|as an AI/i" }, { path: "$.guardrail.verdict", op: "in", value: ["pass", "none", null] }] },
  browser: { steps: [
    { do: "goto", view: "cortex.apps.example.view" },
    { do: "expect_text", text: "Something the screen must show" },
    { do: "click", selector: { role: "button", name: "Open" } },
    { do: "expect_visible", selector: { text: "It opened" } },
    { do: "screenshot", name: "opened", selector: "[id$=\"-root\"]" },
  ] },
};
const browserTemplate = (view) => ({ ...TEMPLATES.browser, steps: TEMPLATES.browser.steps.map((st, i) => (i === 0 && view ? { ...st, view } : st)) });

// A browser test's screenshot, streamed through the authenticated
// /api/tests/artifact route (an <img src> cannot carry the bearer; a presigned
// GET may name a host only the backend reaches): fetch → blob → objectURL.
function Shot({ shot }) {
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!shot?.ref) return undefined;
    let dead = false; let url = "";
    (async () => {
      try {
        const blob = await fetchData(`tests/artifact?key=${encodeURIComponent(shot.ref)}`, { method: "GET", responseType: "blob" });
        url = URL.createObjectURL(blob);
        if (dead) URL.revokeObjectURL(url); else setSrc(url);
      } catch { if (!dead) setFailed(true); }
    })();
    return () => { dead = true; if (url) URL.revokeObjectURL(url); };
  }, [shot?.ref]);
  if (!shot?.ref) return <span className="text-[10px] text-page-muted font-mono">{shot?.name} (not uploaded)</span>;
  if (failed) return <span className="text-[10px] text-page-muted font-mono">{shot.name} (image unavailable)</span>;
  if (!src) return <span className="inline-flex items-center gap-1 text-[10px] text-page-muted"><Loader2 size={10} className="animate-spin" /> {shot.name}…</span>;
  return (
    <a href={src} target="_blank" rel="noreferrer" title={shot.name} data-testid={`test-shot-${shot.name}`} className="inline-block">
      <img src={src} alt={shot.name} className="max-h-40 rounded border border-page-divider" />
    </a>
  );
}

// One verdict, expandable to the checks behind it.
export function ResultDetails({ latest }) {
  const [open, setOpen] = useState(false);
  if (!latest) return null;
  const checks = latest.details?.checks || [];
  const steps = latest.details?.steps || [];
  const shots = latest.details?.screenshots || [];
  return (
    <div className="text-[11px]">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer text-page-muted hover:text-page-text">
        {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />} details{shots.length ? ` · ${shots.length} screenshot${shots.length === 1 ? "" : "s"}` : ""}
      </button>
      {open ? (
        <div className="mt-1 space-y-0.5">
          {steps.length ? (
            <ol className="space-y-0.5" data-testid="test-steps">
              {steps.map((st) => (
                <li key={st.i} className={st.ok ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}>
                  {st.ok ? "✓" : "✗"} <span className="font-mono">{st.do}</span>
                  {st.message ? <span className="text-page-muted"> — {String(st.message).slice(0, 160)}</span> : null}
                  <span className="text-page-muted"> · {st.duration_ms} ms</span>
                </li>
              ))}
            </ol>
          ) : null}
          {shots.length ? <div className="flex flex-wrap gap-2 py-1" data-testid="test-screenshots">{shots.map((sh) => <Shot key={sh.name} shot={sh} />)}</div> : null}
          {checks.length ? checks.map((c, i) => (
            <div key={i} className={c.ok ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}>
              {c.ok ? "✓" : "✗"} <span className="font-mono">{c.path} {c.op}{c.expected !== undefined ? ` ${JSON.stringify(c.expected)}` : ""}</span>
              {!c.ok ? <span className="text-page-muted"> — got <span className="font-mono">{JSON.stringify(c.actual)?.slice(0, 120)}</span></span> : null}
            </div>
          )) : (steps.length ? null : <div className="text-page-muted">no assertions recorded</div>)}
          {latest.details?.subject ? (
            <pre className="text-[10px] whitespace-pre-wrap max-h-48 overflow-auto rounded px-2 py-1 mt-1"
              style={{ background: "var(--table-stripe)" }}>{JSON.stringify(latest.details.subject, null, 1).slice(0, 4000)}</pre>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function TestForm({ featureKey, initial, onSaved, onCancel, appView }) {
  const templateFor = (k) => (k === "browser" ? browserTemplate(appView) : TEMPLATES[k]);
  const [name, setName] = useState(initial?.name || "");
  const [kind, setKind] = useState(initial?.kind || "sql");
  const [description, setDescription] = useState(initial?.description || "");
  const [spec, setSpec] = useState(JSON.stringify(initial?.spec || templateFor(initial?.kind || "sql"), null, 2));
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const changeKind = (k) => { setKind(k); if (!initial) setSpec(JSON.stringify(templateFor(k), null, 2)); };
  const save = async () => {
    setErr("");
    let parsed;
    try { parsed = JSON.parse(spec); } catch (e) { setErr(`spec is not valid JSON: ${e.message}`); return; }
    setBusy(true);
    try {
      const body = { feature_key: featureKey, name, kind, description: description || null, spec: parsed };
      const r = initial?.id
        ? await fetchData(`tests/${initial.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
        : await fetchData("tests", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      onSaved?.(r?.test);
    } catch (e) { setErr(String(e?.message || "could not save the test")); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-page-divider p-2.5 space-y-2 text-xs" data-testid="test-form">
      <div className="flex flex-wrap gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="What this test proves (one sentence)"
          data-testid="test-form-name"
          className="flex-1 min-w-[16rem] px-2 py-1 rounded-md border border-page-divider bg-transparent text-page-text" />
        <select value={kind} onChange={(e) => changeKind(e.target.value)} data-testid="test-form-kind"
          className="px-2 py-1 rounded-md border border-page-divider bg-card-bg text-page-text cursor-pointer">
          {Object.keys(TEMPLATES).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <div className="text-[11px] text-page-muted">{KIND_HELP[kind]} Targets are resolved per environment — the spec never names a host.</div>
      <textarea value={spec} onChange={(e) => setSpec(e.target.value)} rows={7} spellCheck={false} data-testid="test-form-spec"
        className="w-full font-mono text-[11px] px-2 py-1 rounded-md border border-page-divider bg-transparent text-page-text" />
      <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Notes (optional)"
        className="w-full px-2 py-1 rounded-md border border-page-divider bg-transparent text-page-text" />
      {err ? <div className="text-red-600" data-testid="test-form-error">{err}</div> : null}
      <div className="flex gap-2">
        <button type="button" onClick={save} disabled={busy || !name.trim()} data-testid="test-form-save"
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-emerald-500/10 disabled:opacity-50">
          {busy ? <Loader2 size={11} className="animate-spin" /> : null} {initial?.id ? "Save" : "Add test"}
        </button>
        <button type="button" onClick={onCancel} className="px-2.5 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer text-page-muted hover:text-page-text">Cancel</button>
      </div>
    </div>
  );
}

export default function FeatureTests({ projectSlug, feature, tests, generating, onChanged, readOnly }) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const rows = useMemo(() => tests || [], [tests]);
  const fk = feature.key;

  const run = async () => {
    setBusy("run"); setMsg("");
    try {
      const r = await fetchData("tests/run", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ feature_key: fk }) });
      setMsg(r?.already_running ? "A run is already in progress — results land as it finishes." : "Running…");
      onChanged?.({ running: true });
    } catch (e) { setMsg(String(e?.message || "could not start the run")); }
    finally { setBusy(""); }
  };
  const generate = async () => {
    setBusy("gen"); setMsg("");
    try {
      const r = await fetchData("tests/generate", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ feature_key: fk }) });
      setMsg(r?.already_running ? "An agent is already writing tests for this requirement." : "Asked the agent fleet to write tests from the use cases — they appear here when the job lands.");
      onChanged?.({ generating: true });
    } catch (e) { setMsg(String(e?.message || "could not ask for tests")); }
    finally { setBusy(""); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete the test "${t.name}"? Its past results stay in the run history.`)) return;
    setBusy(`del-${t.id}`);
    try { await fetchData(`tests/${t.id}`, { method: "DELETE" }); onChanged?.(); }
    catch (e) { setMsg(String(e?.message || "could not delete")); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid={`feature-tests-${fk}`}>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-page-muted inline-flex items-center gap-1">
          <FlaskConical size={11} /> Tests — prove it, here and in every deployment
        </div>
        <TestsBadge tests={rows} testid={`feature-tests-badge-${fk}`} />
        {generating ? (
          <span className="text-[11px] text-nav-active inline-flex items-center gap-1" data-testid={`feature-tests-generating-${fk}`}>
            <Loader2 size={11} className="animate-spin" /> {generating?.kind === "review_tests" ? "the reviewer is fixing red tests…" : "an agent is writing tests…"}
          </span>
        ) : null}
        {!readOnly ? (
          <div className="ml-auto flex items-center gap-1.5">
            {rows.length ? (
              <button type="button" onClick={run} disabled={busy === "run"} data-testid={`feature-tests-run-${fk}`}
                title="Run this requirement's tests now (in cortex)"
                className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 disabled:opacity-50">
                {busy === "run" ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />} Run
              </button>
            ) : null}
            <button type="button" onClick={generate} disabled={busy === "gen" || !!generating} data-testid={`feature-tests-generate-${fk}`}
              title="Ask the agent fleet to derive tests from this requirement's use cases"
              className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 disabled:opacity-50">
              {busy === "gen" ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Write tests
            </button>
            <button type="button" onClick={() => { setAdding(true); setEditing(null); }} data-testid={`feature-tests-add-${fk}`}
              className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30">
              <Plus size={11} /> Add test
            </button>
            <Link to={createPageUrl("status")} title="Every test of the project, latest run" data-testid={`feature-tests-status-link-${fk}`}
              className="inline-flex items-center gap-1 text-[11px] text-page-muted no-underline hover:underline hover:text-nav-active">
              <Activity size={11} /> Status page
            </Link>
          </div>
        ) : null}
      </div>

      {msg ? <div className="text-[11px] text-page-muted mb-1" data-testid={`feature-tests-msg-${fk}`}>{msg}</div> : null}

      {rows.length ? (
        <ul className="space-y-1">
          {rows.map((t) => {
            const l = t.latest;
            const m = testStatusMeta(l?.status);
            return (
              <li key={t.id || t.key} className="text-xs rounded-md px-2 py-1.5" style={{ background: "var(--table-stripe)" }}
                data-testid={`test-${t.key}`} data-status={l?.status || "none"}>
                {editing?.id === t.id ? (
                  <TestForm featureKey={fk} initial={t} appView={feature.app_view} onSaved={() => { setEditing(null); onChanged?.(); }} onCancel={() => setEditing(null)} />
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <StatusDot status={l?.status} />
                      <span className="text-page-text">{t.name}</span>
                      <span className="text-[10px] px-1 py-0.5 rounded bg-card-border/40 text-page-muted font-mono">{t.kind}</span>
                      {t.source === "agent" ? <span className="text-[10px] text-page-muted" title="Written by an agent from the use cases">agent-written</span> : null}
                      {t.source === "reviewer" ? <span className="text-[10px] text-page-muted" title={t.review_note || "Corrected by the fleet's test reviewer from the failure evidence"} data-testid={`test-reviewed-${t.key}`}>reviewed</span> : null}
                      {t.enabled === false ? <span className="text-[10px] text-page-muted" title={t.review_note || ""}>disabled{t.review_note ? " — " + t.review_note.slice(0, 120) : ""}</span> : null}
                      <span className={`text-[11px] ${m.text}`} data-testid={`test-status-${t.key}`}>
                        {l ? `${m.label}${l.duration_ms != null ? ` · ${l.duration_ms} ms` : ""} · ${l.env || "cortex"} · ${when(l.run_at)}` : "never run"}
                      </span>
                      {!readOnly ? (
                        <span className="ml-auto inline-flex items-center gap-1">
                          <button type="button" onClick={() => { setEditing(t); setAdding(false); }} title="Edit" data-testid={`test-edit-${t.key}`}
                            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0.5"><Pencil size={11} /></button>
                          <button type="button" onClick={() => remove(t)} disabled={busy === `del-${t.id}`} title="Delete" data-testid={`test-delete-${t.key}`}
                            className="text-page-muted hover:text-red-700 dark:hover:text-red-400 bg-transparent border-0 cursor-pointer p-0.5 disabled:opacity-50"><Trash2 size={11} /></button>
                        </span>
                      ) : null}
                    </div>
                    {l?.message ? <div className={`text-[11px] mt-0.5 ${l.status === "passed" ? "text-page-muted" : m.text}`}>{l.message}</div> : null}
                    {t.description ? <div className="text-[11px] text-page-muted mt-0.5">{t.description}</div> : null}
                    <ResultDetails latest={l} />
                  </>
                )}
              </li>
            );
          })}
        </ul>
      ) : !adding ? (
        <div className="text-xs text-page-muted" data-testid={`feature-tests-empty-${fk}`}>
          No tests yet. <b>Write tests</b> derives them from the use cases above; <b>Add test</b> writes one by hand.
          The same tests run inside cortex and inside every deployable archive of this project.
        </div>
      ) : null}

      {adding ? (
        <div className="mt-1.5">
          <TestForm featureKey={fk} appView={feature.app_view} onSaved={() => { setAdding(false); onChanged?.(); }} onCancel={() => setAdding(false)} />
        </div>
      ) : null}
    </div>
  );
}
